import { useCallback, useMemo, useState } from "react";

import { useMutation, useQuery } from "convex/react";

import { isSpecRelayIntent } from "@domain/intent-relay";
import { deriveRelayDisplayStatus } from "@domain/intent-relays-status";
import { taskRelayFailed, taskRelayPodUnavailable } from "@domain/user-errors";

import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";

/**
 * The WRITE surface for the workflow Pause/Resume toggle
 * (workflow-pause-toggle spec §Phase 4) — `useTaskWrites` lite, scoped
 * to one spec. Fires `intentRelays.requestPauseWorkflow` /
 * `requestActivateWorkflow` with a hook-minted `clientRequestId`.
 *
 * Two pending signals, truth wins (§4.12 convention):
 *   A — durable relay rows (`listActiveForCurrentUser` filtered to the
 *       spec intents for this specId; cleared when the server computes
 *       `satisfied`, i.e. the projected `specs.userPaused` actually
 *       flipped — never optimistically).
 *   B — unsettled mutation promises covering the click→row gap.
 *
 * Staleness (> 3 min unconfirmed) derives AT RENDER via
 * `deriveRelayDisplayStatus` — no client timers (frontend-doctrine §8).
 */

export interface WorkflowPauseToggleResult {
  /** True while a pause/activate relay for this spec is in flight. */
  readonly pending: boolean;
  /** Inline error copy from the most recent failed relay (or throw). */
  readonly error: string | null;
  /** Stale (> 3 min unconfirmed) pending relay — soft caption. */
  readonly stale: boolean;
  pauseWorkflow(): Promise<void>;
  activateWorkflow(): Promise<void>;
}

const POD_OFFLINE_REASONS = new Set([
  "pod_unavailable",
  "env_missing",
  "timeout",
]);

function failureCopy(reason: string | undefined): string {
  return reason !== undefined && POD_OFFLINE_REASONS.has(reason)
    ? taskRelayPodUnavailable()
    : taskRelayFailed();
}

export function useWorkflowPauseToggle({
  specId,
}: {
  specId: string | null;
}): WorkflowPauseToggleResult {
  const relays = useQuery(api.intentRelays.listActiveForCurrentUser, {});
  const requestPause = useMutation(api.intentRelays.requestPauseWorkflow);
  const requestActivate = useMutation(api.intentRelays.requestActivateWorkflow);

  const [localCount, setLocalCount] = useState(0);
  const [throwError, setThrowError] = useState<string | null>(null);

  const specRelays = useMemo(
    () =>
      (relays ?? []).filter(
        (r) =>
          isSpecRelayIntent(r.intent) &&
          r.targetSpecId !== undefined &&
          r.targetSpecId === specId,
      ),
    [relays, specId],
  );

  const now = Date.now();
  // Toggles do NOT latch: a landed pause reads unsatisfied again after
  // a later resume, so only the NEWEST spec-intent row represents the
  // user's current toggle attempt — older rows are history and must
  // never gate the button (a dispatched-then-undone row would otherwise
  // show "Working…" until the 24h sweep; live dev finding 2026-07-16).
  // A newest-row that goes stale (> 3 min unconfirmed) also un-gates,
  // mirroring ExecutionTab's un-wedge-the-button guard.
  const newest = specRelays.reduce<(typeof specRelays)[number] | undefined>(
    (max, r) => (max === undefined || r.requestedAt > max.requestedAt ? r : max),
    undefined,
  );
  const newestUnconfirmed =
    newest !== undefined && !newest.satisfied && newest.status !== "failed";
  const stale =
    newestUnconfirmed &&
    deriveRelayDisplayStatus({ relay: newest, now }) === "stale";
  const pending = localCount > 0 || (newestUnconfirmed && !stale);

  const failedRow =
    !pending && newest !== undefined && newest.status === "failed" && !newest.satisfied
      ? newest
      : undefined;
  const error =
    throwError ??
    (failedRow !== undefined ? failureCopy(failedRow.failureReason) : null);

  const fire = useCallback(
    async (send: (clientRequestId: string) => Promise<unknown>) => {
      if (specId === null) return;
      const clientRequestId = crypto.randomUUID();
      setThrowError(null);
      setLocalCount((n) => n + 1);
      try {
        await send(clientRequestId);
      } catch (err) {
        // INVALID_STATE = the flag already flipped (stale button); truth
        // is about to refresh the pill — no error chip for that.
        const msg = err instanceof Error ? err.message : String(err);
        if (!msg.includes("INVALID_STATE")) {
          setThrowError(taskRelayFailed());
        }
      } finally {
        setLocalCount((n) => n - 1);
      }
    },
    [specId],
  );

  const pauseWorkflow = useCallback(
    () =>
      fire((clientRequestId) =>
        requestPause({ specId: specId as Id<"specs">, clientRequestId }),
      ),
    [fire, requestPause, specId],
  );
  const activateWorkflow = useCallback(
    () =>
      fire((clientRequestId) =>
        requestActivate({ specId: specId as Id<"specs">, clientRequestId }),
      ),
    [fire, requestActivate, specId],
  );

  return { pending, error, stale, pauseWorkflow, activateWorkflow };
}
